// Lógica pura do terminal interativo no navegador (6.1): sem DOM e sem xterm, testada em
// tests/pty-web.test.ts. A tela e as janelas estão em src/web/pty.ts.

/** Sessão como chega de GET /api/pty (espelha PtyInfo de src/core/pty.ts). */
export interface PtySessao {
  id: string;
  cli: 'claude' | 'codex';
  cwd: string;
  sessionId?: string;
  account?: string;
  created: number;
  cols: number;
  rows: number;
}

/** Resposta de GET /api/pty. */
export interface PtyEstadoApi {
  ok?: boolean;
  enabled?: boolean;
  totp?: boolean;
  /** O node-pty carregou no servidor? */
  available?: boolean;
  /** Admin com TOTP e interruptor ligado. */
  allowed?: boolean;
  reason?: string;
  sessions?: PtySessao[];
}

/** O que o agente do escritório sabe de si, para achar o pty dele. */
export interface AgenteRef {
  provider: string;
  sessionId: string;
  cwd: string;
}

/**
 * O pty que roda este agente: o que retomou a mesma sessão ('claude --resume') ou, na falta, o da
 * mesma CLI na mesma pasta (a sessão retomada pode ganhar outro id). Vários na mesma pasta: o mais novo.
 */
export function sessaoDoAgente(sessoes: readonly PtySessao[], agente: AgenteRef): PtySessao | undefined {
  const porId = sessoes.find((s) => s.sessionId && s.sessionId === agente.sessionId);
  if (porId) return porId;
  return [...sessoes].filter((s) => s.cli === agente.provider && s.cwd === agente.cwd).sort((a, b) => b.created - a.created)[0];
}

export type Uso = { usavel: true } | { usavel: false; motivo: 'indisponivel' | 'bloqueado' | 'desconhecido'; texto?: string };

/** Dá para usar o terminal interativo? 'indisponivel' = sem node-pty (segue o tmux); 'bloqueado' = trava do servidor. */
export function usoDoPty(estado: PtyEstadoApi | undefined): Uso {
  if (!estado?.ok) return { usavel: false, motivo: 'desconhecido' };
  if (!estado.allowed) return { usavel: false, motivo: 'bloqueado', ...(estado.reason ? { texto: estado.reason } : {}) };
  if (!estado.available) return { usavel: false, motivo: 'indisponivel' };
  return { usavel: true };
}

/** Pastas para sugerir na Nova sessão: as dos agentes, sem repetir, as mais usadas primeiro. */
export function pastasSugeridas(agentes: ReadonlyArray<{ cwd: string }>): string[] {
  const contagem = new Map<string, number>();
  for (const a of agentes) if (a.cwd) contagem.set(a.cwd, (contagem.get(a.cwd) ?? 0) + 1);
  return [...contagem.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([cwd]) => cwd);
}

/** Teclas dos botões do celular (o teclado virtual não tem Esc, Tab, setas nem Ctrl). */
export const TECLAS: Record<string, string> = {
  esc: '\x1b',
  tab: '\t',
  cima: '\x1b[A',
  baixo: '\x1b[B',
  direita: '\x1b[C',
  esquerda: '\x1b[D',
  'ctrl-c': '\x03',
  'ctrl-d': '\x04',
  enter: '\r',
};

/** Confirmação em dois cliques: o primeiro arma, o segundo (dentro do prazo) dispara. */
export class DoisCliques {
  private armadoAte = 0;

  constructor(private readonly prazoMs: number = 4000) {}

  /** true = é o segundo clique e vale. */
  clica(agora: number = Date.now()): boolean {
    if (this.armadoAte > 0 && agora <= this.armadoAte) {
      this.armadoAte = 0;
      return true;
    }
    this.armadoAte = agora + this.prazoMs;
    return false;
  }

  get armado(): boolean {
    return this.armadoAte > 0;
  }

  desarma(): void {
    this.armadoAte = 0;
  }
}

/** Nome curto de uma sessão para a aba: 'claude · meu-projeto'. */
export function rotuloDaSessao(s: Pick<PtySessao, 'cli' | 'cwd'>): string {
  const pasta = s.cwd.replace(/[\\/]+$/, '').split(/[\\/]/).pop() || s.cwd;
  return `${s.cli} · ${pasta}`;
}
